import { ChevronRightIcon } from '@chakra-ui/icons'
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import _ from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDots } from 'react-icons/bs'
import type { ViewableParentSuccessResponse } from '../../../../interfaces'
import {
  MediaLibraryActions,
  MLContext,
} from '../../../../providers/MLProvider'

interface BreadcrumbData {
  path: string
  folderName?: string
}

interface Props {
  onCloseMovePopup?(): void
}
const Breadcrumbs = ({ onCloseMovePopup }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [{ drawerFolderPath, drawerViewableParent }, dispatch] =
    useContext(MLContext)
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbData[]>()

  const sanitizePathUrl = React.useCallback(
    (url: string): string => {
      if (window.isMediaLibraryWidget && window.widgetHost) {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}widgetHost=${window.widgetHost}${window.widgetImagekitId
            ? `&widgetImagekitId=${window.widgetImagekitId}`
            : ''
          }`
      }

      return url
    },
    [], // No dependencies needed
  )

  const getBreadcrumbData = (): void => {
    let viewableFolderPath = drawerFolderPath
    let unViewableBasePath = ''
    if (
      drawerViewableParent &&
      !_.isUndefined(
        (drawerViewableParent as ViewableParentSuccessResponse).asset,
      )
    ) {
      const index = (
        drawerViewableParent as ViewableParentSuccessResponse
      ).asset?.assetPath.lastIndexOf('/')
      if (index) {
        viewableFolderPath = drawerFolderPath.slice(
          index,
          viewableFolderPath.length,
        )
        unViewableBasePath = drawerFolderPath.slice(0, index)
      }
    }
    const splitPath =
      viewableFolderPath === '/' ? [''] : viewableFolderPath.split('/')
    const data: BreadcrumbData[] = []

    data.push({
      path: sanitizePathUrl(`/${splitPath[0]}`),
      folderName: splitPath[0] === '' ? t('home', 'Home') : splitPath[0],
    })
    if (splitPath.length > 1) {
      for (let i = 1; i < splitPath.length; i++) {
        if (i === 1) {
          data.push({
            path: sanitizePathUrl(`${unViewableBasePath}/${splitPath[i]}`),
            folderName: splitPath[i],
          })
        } else {
          data.push({
            path: sanitizePathUrl(`${data[data.length - 1].path}/${splitPath[i]}`),
            folderName: splitPath[i],
          })
        }
      }
    }
    setBreadcrumbData(data)
  }

  useEffect(() => {
    getBreadcrumbData()
  }, [drawerFolderPath, drawerViewableParent])

  return (
    <Breadcrumb
      spacing="0.1em"
      separator={<ChevronRightIcon />}
      color="appClearBlue"
      fontWeight="semibold"
    >
      {breadcrumbData && breadcrumbData.length <= 2 ? (
        breadcrumbData.map(data => (
          <BreadcrumbItem key={data.folderName}>
            <Tooltip label={data.folderName}>
              <BreadcrumbLink
                color="appClearBlue"
                fontWeight="semibold"
                onClick={() => {
                  dispatch({
                    type: MediaLibraryActions.SET_DRAWER_FOLDER,
                    payload: data.path,
                  })

                  if (onCloseMovePopup) {
                    onCloseMovePopup()
                  }
                }}
                maxW="8em"
                isTruncated
              >
                {data.folderName}
              </BreadcrumbLink>
            </Tooltip>
          </BreadcrumbItem>
        ))
      ) : breadcrumbData && breadcrumbData.length > 2 ? (
        <>
          <BreadcrumbItem key={breadcrumbData[0].folderName}>
            <BreadcrumbLink
              color="appClearBlue"
              fontWeight="semibold"
              onClick={() => {
                dispatch({
                  type: MediaLibraryActions.SET_DRAWER_FOLDER,
                  payload: breadcrumbData[0].path,
                })

                if (onCloseMovePopup) {
                  onCloseMovePopup()
                }
              }}
            >
              {breadcrumbData[0].folderName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator margin="0 0.1em" as={ChevronRightIcon} />
          <BreadcrumbItem>
            <BreadcrumbLink fontWeight="semibold">
              <Popover trigger="hover">
                <PopoverTrigger>
                  <IconButton
                    aria-label="More Folders"
                    icon={<Icon as={BsThreeDots} />}
                    variant="unstyled"
                    boxShadow="none"
                    marginX="0"
                    paddingX="0"
                    minW="min-content"
                    color="appClearBlue"
                  />
                </PopoverTrigger>
                <PopoverContent maxWidth="unset" width="unset" maxH="200px" overflowY="auto">
                  <PopoverArrow />
                  <PopoverBody padding="0">
                    <VStack paddingY={1}>
                      {breadcrumbData
                        .slice(1, breadcrumbData.length - 1)
                        .map(data => (
                          <Box
                            key={data.path}
                            onClick={() => {
                              dispatch({
                                type: MediaLibraryActions.SET_DRAWER_FOLDER,
                                payload: data.path,
                              })

                              if (onCloseMovePopup) {
                                onCloseMovePopup()
                              }
                            }}
                            paddingX={2}
                            paddingY={2}
                            width="100%"
                            _hover={{
                              backgroundColor: 'appGrey',
                            }}
                            fontWeight="normal"
                            color="appDarkIndigo"
                          >
                            {data.folderName}
                          </Box>
                        ))}
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator margin="0em 0.1em" as={ChevronRightIcon} />
          <BreadcrumbItem
            key={breadcrumbData[breadcrumbData.length - 1].folderName}
          >
            <Tooltip
              label={breadcrumbData[breadcrumbData.length - 1].folderName}
            >
              <BreadcrumbLink
                color="appClearBlue"
                fontWeight="semibold"
                onClick={() => {
                  dispatch({
                    type: MediaLibraryActions.SET_DRAWER_FOLDER,
                    payload: breadcrumbData[breadcrumbData.length - 1].path,
                  })

                  if (onCloseMovePopup) {
                    onCloseMovePopup()
                  }
                }}
                maxW="8em"
                isTruncated
              >
                {breadcrumbData[breadcrumbData.length - 1].folderName}
              </BreadcrumbLink>
            </Tooltip>
          </BreadcrumbItem>
        </>
      ) : null}
    </Breadcrumb>
  )
}

export default Breadcrumbs
